import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/**
 * Iter-93 / Iter-102 PART 4 — Schedule upload dialog.
 *
 * Simple document-store flow: the file itself is the descriptor.
 * Owner picks a file, optionally renames it (Title), and uploads.
 * No required date, no shoot-day field, no "Day plan" placeholder —
 * just the minimum the user actually needs.
 */
export default function AddScheduleDialog({ open, onOpenChange, projectId, onCreated }) {
    const apiBase = useApiBase();
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const reset = () => {
        setFile(null);
        setTitle("");
        setError("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async () => {
        if (!file) {
            setError("Pick a file first.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            // Iter-102 PART 4 — shoot_date is optional server-side,
            // shoot_day field is removed entirely. Title falls back to
            // the filename on the server when left blank.
            if (title.trim()) fd.append("title", title.trim());
            const { data } = await api.post(
                `${apiBase}/${projectId}/schedules`,
                fd,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 120000,
                },
            );
            toast.success(`Schedule added · ${data.title || data.filename}`);
            onCreated?.(data);
            reset();
            onOpenChange(false);
        } catch (e) {
            const msg = formatApiError(e) || "Schedule upload failed. Please try again.";
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
            <DialogContent
                data-testid="add-schedule-dialog"
                className="max-w-md bg-card border-white/10"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white">
                        Add schedule
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        PDF, DOCX or TXT. The filename is kept as the descriptor — optionally rename it below.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 mt-2">
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            File
                        </Label>
                        <input
                            ref={fileRef}
                            type="file"
                            data-testid="add-schedule-file-input"
                            accept=".pdf,.docx,.txt"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                        <Button
                            type="button"
                            data-testid="add-schedule-pick-file"
                            variant="secondary"
                            className="mt-1.5 w-full min-tap"
                            onClick={() => fileRef.current?.click()}
                        >
                            <Upload size={14} className="mr-2 shrink-0" />
                            <span className="truncate">{file ? file.name : "Choose file"}</span>
                        </Button>
                    </div>

                    <div>
                        <Label
                            htmlFor="add-schedule-title"
                            className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                        >
                            Title (optional)
                        </Label>
                        <Input
                            id="add-schedule-title"
                            data-testid="add-schedule-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={file ? file.name : "Leaves filename as title"}
                            className="mt-1.5 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                        />
                    </div>

                    {error ? (
                        <p data-testid="add-schedule-error" className="text-xs text-red-400">
                            {error}
                        </p>
                    ) : null}

                    {busy ? (
                        <div
                            data-testid="add-schedule-progress"
                            className="flex items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-3 text-sm text-white"
                        >
                            <Loader2 size={16} className="animate-spin text-primary shrink-0" />
                            <span>Uploading schedule…</span>
                        </div>
                    ) : (
                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                data-testid="add-schedule-cancel"
                                variant="secondary"
                                onClick={() => onOpenChange(false)}
                                className="flex-1 min-tap"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                data-testid="add-schedule-submit"
                                onClick={submit}
                                disabled={!file}
                                className="flex-1 min-tap"
                            >
                                Upload
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
